import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useNavLinksContext } from '../contexts/NavLinksContext';
import { useAppContext } from '../contexts/Storage';
import { useSupportContext } from '../components/Support';
import { useProfile, useSubscription } from '../components/Profile';
import { useUpdateUser } from '../api/users';
import { useGetCreditBalance } from '../api/credits';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import ThemeToggle from '../components/utils/ThemeToggle';
import userIcon from '../assets/images/user.png';
import emailIcon from '../assets/images/email.png';
import calendarIcon from '../assets/images/calendar.png';
import lockIcon from '../assets/images/hide.png';
import cameraIcon from '../assets/images/user.png';
import creditPointsIcon from '../assets/images/points.png';
import helpIcon from '../assets/images/question.png';
import infoIcon from '../assets/images/information_green.png';
import privacyIcon from '../assets/images/padlock.png';
import contactIcon from '../assets/images/phone-call.png';
import subscriptionIcon from '../assets/images/crown.png';
import profilePlaceholder from '../assets/images/profile_placeholder.png';
import aiIcon from '../assets/images/bot.png';
import settingsIcon from '../assets/images/settings.png';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { setNavLinks } = useNavLinksContext();
  const { user } = useAppContext();
  const { openSupport } = useSupportContext();

  const {
    formData,
    isEditing,
    isUpdating,
    updateError,
    updateFormField,
    handleSaveProfile,
    handleCancelEdit,
    startEditing,
  } = useProfile({
    onUpdateSuccess: (data) => {
      toast.success('Profile updated successfully!');
      if (data.user?.image_url) {
        setProfileImage(data.user.image_url);
      }
    },
    onUpdateError: (error) => {
      toast.error(error.message || 'Failed to update profile');
    },
  });

  const {
    mappedData: subscriptionData,
    isLoading: isLoadingSubscription,
  } = useSubscription();

  const {
    data: creditBalance,
    isLoading: isLoadingCredits,
  } = useGetCreditBalance();

  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [profileImage, setProfileImage] = useState<string>(profilePlaceholder);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const [timezone, setTimezone] = useState('America/New_York');
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    setNavLinks({
      links: [
        { to: '/dashboard', label: 'Dashboard' },
        { to: '/profile', label: 'Profile & Settings' },
      ],
    });
  }, [setNavLinks]);

  useEffect(() => {
    if (user?.imageUrl) {
      setProfileImage(user.imageUrl);
    }
  }, [user?.imageUrl]);

  const changePasswordMutation = useUpdateUser({
    onSuccess: () => {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
      setShowPasswordReset(false);
      toast.success('Password updated successfully!');
    },
    onError: (error) => {
      const msg = error.message || 'Failed to update password';
      setPasswordError(msg);
      toast.error(msg);
    },
  });

  const handlePasswordReset = async () => {
    setPasswordError('');

    if (!newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        email: user?.email || '',
        password: newPassword,
        password_confirmation: confirmPassword,
      });
    } catch {
      // Error is already handled by the mutation's onError callback
    }
  };

  const handleProfileImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setIsUploadingImage(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setProfileImage(event.target.result as string);
        updateFormField('imageUrl', event.target.result as string);
        setIsUploadingImage(false);
        toast.info('Profile picture selected. Click Save Changes to persist.');
      }
    };
    reader.onerror = () => {
      setIsUploadingImage(false);
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleBuyCreditPoints = () => {
    navigate('/credit-points');
  };

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'GMT (London)' },
    { value: 'Europe/Paris', label: 'CET (Paris)' },
    { value: 'Asia/Tokyo', label: 'JST (Tokyo)' },
    { value: 'Asia/Singapore', label: 'SGT (Singapore)' },
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'ja', label: 'Japanese' },
    { value: 'zh', label: 'Chinese' },
  ];

  const displayName = `${formData.firstName || user?.username || ''} ${formData.lastName || ''}`.trim() || 'User';

  return (
    <div className="h-shink-from-navbar h-fill-from-navbar px-2 bg-bg_grey_light text-dark overflow-y-scroll">
      <div className="">
        <div className="p-6 bg-light rounded-t-lg mb-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              {isEditing && (
                <label
                  htmlFor="profile-upload"
                  className="absolute bottom-2 right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary_dark transition-colors shadow-md"
                >
                  <img src={cameraIcon} alt="Camera" className="w-5 h-5" />
                  <input
                    id="profile-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleProfileImageUpload}
                    disabled={isUploadingImage}
                  />
                </label>
              )}
              {isUploadingImage && (
                <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                  <div className="text-white text-sm">Uploading...</div>
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-2xl font-bold">{displayName}</h3>
                  <p className="text-grey_dark mt-1">{formData.email}</p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center">
                      <img src={emailIcon} alt="Email" className="w-4 h-4 mr-2" />
                      <span className="text-sm">{formData.email}</span>
                    </div>
                    <div className="flex items-center">
                      <img src={calendarIcon} alt="Joined" className="w-4 h-4 mr-2" />
                      <span className="text-sm">Member</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        onClick={() => {
                          handleCancelEdit();
                          setProfileImage(user?.imageUrl || profilePlaceholder);
                        }}
                        variant="google"
                        className="text-grey_dark"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSaveProfile}
                        variant="filled-primary"
                        className="flex items-center"
                        disabled={isUpdating}
                      >
                        {isUpdating ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={startEditing}
                      variant="filled-primary"
                    >
                      Edit Profile
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {updateError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {updateError.message}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={userIcon} alt="Profile" className="w-5 h-5 mr-2" />
                Personal Information
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Username</label>
                  <Input
                    type="text"
                    attributes={{
                      value: formData.username,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        updateFormField('username', e.target.value),
                      disabled: !isEditing,
                      placeholder: 'Username',
                    }}
                    leftIcon={userIcon}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">First Name</label>
                  <Input
                    type="text"
                    attributes={{
                      value: formData.firstName,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        updateFormField('firstName', e.target.value),
                      disabled: !isEditing,
                      placeholder: 'First name',
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Last Name</label>
                  <Input
                    type="text"
                    attributes={{
                      value: formData.lastName,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        updateFormField('lastName', e.target.value),
                      disabled: !isEditing,
                      placeholder: 'Last name',
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <Input
                    type="email"
                    attributes={{
                      value: formData.email,
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                        updateFormField('email', e.target.value),
                      disabled: !isEditing,
                      placeholder: 'Email address',
                    }}
                    leftIcon={emailIcon}
                  />
                </div>
              </div>
            </div>

            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={lockIcon} alt="Security" className="w-5 h-5 mr-2" />
                Security & Password
              </h4>

              {showPasswordReset ? (
                <div className="space-y-4">
                  {passwordError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
                      {passwordError}
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium mb-2">New Password</label>
                    <Input
                      type="password"
                      attributes={{
                        value: newPassword,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                          setNewPassword(e.target.value),
                        placeholder: 'Enter new password',
                      }}
                      showPasswordToggle
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                    <Input
                      type="password"
                      attributes={{
                        value: confirmPassword,
                        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                          setConfirmPassword(e.target.value),
                        placeholder: 'Confirm new password',
                      }}
                      showPasswordToggle
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handlePasswordReset}
                      variant="filled-primary"
                      disabled={changePasswordMutation.isPending}
                    >
                      {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowPasswordReset(false);
                        setPasswordError('');
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      variant="google"
                      className="text-grey_dark"
                      disabled={changePasswordMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-grey_dark mb-4">Change your password to keep your account secure</p>
                  <Button onClick={() => setShowPasswordReset(true)} variant="bordered-primary">
                    Change Password
                  </Button>
                  <div className="mt-4 pt-4 border-t border-grey_light">
                    <Button
                      onClick={() => navigate('/forgot-password')}
                      variant="google"
                      className="text-grey_dark w-full"
                    >
                      Forgot Password?
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={aiIcon} alt="AI" className="w-5 h-5 mr-2" />
                AI Customization
              </h4>
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-grey_light rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src={aiIcon} alt="AI" className="w-8 h-8" />
                </div>
                <h5 className="text-lg font-medium mb-2">AI Preferences</h5>
                <p className="text-grey_dark mb-6">
                  Customize how AI assists you in the application. This feature is coming soon!
                </p>
                <Button
                  variant="bordered-primary"
                  className="w-full"
                  onClick={() => alert('AI customization coming soon!')}
                >
                  Configure AI Settings
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={subscriptionIcon} alt="Subscription" className="w-5 h-5 mr-2" />
                Subscription & Credits
              </h4>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-grey_dark">Current Plan</div>
                  <div className="flex items-center justify-between">
                    {isLoadingSubscription ? (
                      <span className="font-medium text-grey_dark">Loading...</span>
                    ) : (
                      <span className="font-medium">
                        {subscriptionData?.planDisplayName || 'Free Plan'}
                      </span>
                    )}
                    <Button
                      onClick={() => navigate('/subscription')}
                      variant="bordered-primary"
                      className="text-xs py-1 px-3"
                    >
                      Manage
                    </Button>
                  </div>
                  {subscriptionData?.isActive && (
                    <div className="text-xs text-primary mt-1">
                      ✓ Active subscription
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-sm text-grey_dark">Credit Points Balance</div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <img src={creditPointsIcon} alt="Credits" className="w-5 h-5 mr-2" />
                      {isLoadingCredits ? (
                        <span className="text-2xl font-bold text-grey_dark">...</span>
                      ) : (
                        <span className="text-2xl font-bold">
                          {creditBalance?.toLocaleString() ?? 0}
                        </span>
                      )}
                      <span className="text-grey_dark ml-2">points</span>
                    </div>
                    <Button
                      onClick={handleBuyCreditPoints}
                      variant="filled-primary"
                      className="text-xs py-1 px-3"
                    >
                      Buy More
                    </Button>
                  </div>
                  <div className="mt-2">
                    <Button
                      onClick={() => navigate('/credit-points')}
                      variant="google"
                      className="text-grey_dark w-full text-sm"
                    >
                      View Credit History →
                    </Button>
                  </div>
                </div>

                <div className="pt-4 border-t border-grey_light">
                  <div className="text-sm text-grey_dark mb-2">Subscription Activity</div>
                  {subscriptionData?.startDate && (
                    <div className="text-xs text-grey_dark mb-2">
                      Started: {new Date(subscriptionData.startDate).toLocaleDateString()}
                    </div>
                  )}
                  <Button
                    onClick={() => navigate('/subscription')}
                    variant="bordered-primary"
                    className="w-full"
                  >
                    View Subscription Details
                  </Button>
                </div>
              </div>
            </div>

            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={settingsIcon} alt="Settings" className="w-5 h-5 mr-2" />
                Account Settings
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Timezone</label>
                  <Select
                    options={timezoneOptions}
                    value={timezone}
                    onChange={(value) => setTimezone(value)}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Language</label>
                  <Select
                    options={languageOptions}
                    value={language}
                    onChange={(value) => setLanguage(value)}
                    disabled={!isEditing}
                  />
                </div>

                <div className="pt-4 border-t border-grey_light">
                  <div className="flex items-center justify-between">
                    <ThemeToggle />
                  </div>
                </div>

                <div className="pt-4 border-t border-grey_light">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-grey_light text-primary focus:ring-primary"
                      defaultChecked
                    />
                    <span className="ml-2 text-sm">Email notifications</span>
                  </label>
                  <label className="flex items-center mt-3">
                    <input
                      type="checkbox"
                      className="rounded border-grey_light text-primary focus:ring-primary"
                      defaultChecked
                    />
                    <span className="ml-2 text-sm">SMS notifications</span>
                  </label>
                  <label className="flex items-center mt-3">
                    <input
                      type="checkbox"
                      className="rounded border-grey_light text-primary focus:ring-primary"
                    />
                    <span className="ml-2 text-sm">Marketing emails</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={infoIcon} alt="Info" className="w-5 h-5 mr-2" />
                Account Information
              </h4>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-grey_dark">Account ID</div>
                  <div className="font-mono text-sm">{user?.id || 'N/A'}</div>
                </div>
                <div>
                  <div className="text-sm text-grey_dark">Username</div>
                  <div className="flex items-center">
                    <img src={userIcon} alt="User" className="w-4 h-4 mr-2" />
                    {user?.username || 'N/A'}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-6 flex items-center">
                <img src={helpIcon} alt="Help" className="w-5 h-5 mr-2" />
                Support & Legal
              </h4>

              <div className="space-y-3">
                <Button
                  variant="google"
                  className="w-full text-grey_dark hover:text-dark flex justify-center items-center"
                  onClick={() => window.open('/privacy-policy', '_blank')}
                >
                  <img src={privacyIcon} alt="Privacy" className="w-4 h-4 mr-2 inline-block align-middle" />
                  <div className="align-middle text-dark min-w-[115px] text-start">Privacy Policy</div>
                </Button>
                <Button
                  variant="google"
                  className="w-full text-grey_dark hover:text-dark flex justify-center items-center"
                  onClick={() => alert('About Us')}
                >
                  <img src={infoIcon} alt="About" className="w-4 h-4 mr-2 inline-block align-middle" />
                  <div className="align-start text-dark min-w-[115px] text-start">About Us</div>
                </Button>
                <Button
                  variant="google"
                  className="w-full text-grey_dark hover:text-dark flex justify-center items-center"
                  onClick={openSupport}
                >
                  <img src={contactIcon} alt="Contact" className="w-4 h-4 mr-2 inline-block align-middle" />
                  <div className="align-middle text-dark min-w-[115px] text-start">Contact Us</div>
                </Button>
                <Button
                  variant="google"
                  className="w-full text-grey_dark hover:text-dark flex justify-center items-center"
                  onClick={openSupport}
                >
                  <img src={helpIcon} alt="Help" className="w-4 h-4 mr-2 inline-block align-middle" />
                  <div className="align-middle text-dark min-w-[115px] text-start">Help Center</div>
                </Button>
              </div>
            </div>

            <div className="bg-light rounded-lg p-6">
              <h4 className="text-lg font-medium mb-4 text-dark">Danger Zone</h4>
              <p className="text-sm text-dark mb-4">
                These actions are irreversible. Please proceed with caution.
              </p>
              <div className="space-y-3">
                <Button
                  variant="bordered-danger"
                  className="w-full"
                  onClick={() => alert('Export requested')}
                >
                  Export All Data
                </Button>
                <Button
                  variant="filled-primary"
                  className="w-full bg-danger hover:bg-danger_dark border-danger"
                  onClick={() => {
                    const res = window.confirm(
                      'Are you sure you want to delete your account? This action cannot be undone.'
                    );
                    if (res === true) {
                      alert('Account deletion requested');
                    }
                  }}
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
